import React from 'react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'

import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import TextField from '@material-ui/core/TextField'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import InputLabel from '@material-ui/core/InputLabel'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
// import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'

import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'

import { makeStyles } from '@material-ui/core/styles'

import { productSearch } from 'layouts/Aws'

const useStyles = makeStyles({
  tableContainer: {
    maxHeight: '400px'
  }
})

export default function SelectProductDialog (props) {
  const {
    title,
    open,
    onProductSelect,
    onClose,
    siteId
  } = props

  const classes = useStyles()

  const searchOperators = ['starts with', 'contains', 'equals']
  const types = ['All', 'Variable', 'Priced', 'Price Embedded']

  const [searchValue, setSearchValue] = React.useState('')
  const [isSearching, setSearching] = React.useState(false)
  const [selectedProduct, setSelectedProduct] = React.useState(false)
  const [searchOperator, setSearchOperator] = React.useState(searchOperators[0])
  const [type, setType] = React.useState(types[0])
  const [products, setProducts] = useImmer([])
  const [maxProductsExceeded, setMaxProductsExceeded] = React.useState(false)

  const searchOperatorNodes = searchOperators.map((searchOperator) => <MenuItem key={searchOperator} value={searchOperator}>{searchOperator}</MenuItem>)
  const typeNodes = types.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)

  React.useEffect(() => {
    resetDialog()
  }, [open])

  const resetDialog = () => {
    setSearchValue('')
    setSearching(false)
    setSelectedProduct(null)
    setProducts([])
    setMaxProductsExceeded(false)
    setSearchOperator(searchOperators[0])
    setType(types[0])
  }

  const handleClose = (event, reason) => {
    // don't close by clicking background
    if (reason === 'backdropClick') {
      return
    }

    onClose(event, reason)
  }

  const handleSearch = async () => {
    await searchForProducts()
  }

  const searchForProducts = async () => {
    setSearching(true)

    setProducts([])
    setMaxProductsExceeded(false)

    const response = await productSearch(siteId, searchValue, searchOperator, type)

    setProducts(response.products)
    setMaxProductsExceeded(response.maxProductsExceeded)

    setSearching(false)
  }

  const handleProductRowClick = (clickedProduct) => {
    setProducts((draft) => {
      for (const product of draft) {
        product.selected = product.productId === clickedProduct.productId
      }
    })

    setSelectedProduct(clickedProduct)
  }

  // search upon pressing enter in the searchValue input box
  const handleKeyPress = async (event) => {
    if (event.key === 'Enter') {
      if (searchValue.length >= 3 && !isSearching) {
        await searchForProducts()
      }
    }
  }

  const productRows = products.map((product) => {
    let price = null

    console.log('product type:', product)

    if (product.type === 'priced') {
      price = `£${(product.price / 100).toFixed(2)}`
    } else if (product.type === 'variable') {
      price = 'Variable'
    } else if (product.type === 'price_embedded') {
      price = 'Price Embedded'
    }

    return (
      <TableRow key={product.productId} hover onClick={(event) => handleProductRowClick(product)} selected={product.selected}>
        <TableCell>{product.barcode !== null ? product.barcode : 'Unbarcoded'}</TableCell>
        <TableCell>{product.productDescription === product.originalProductDescription ? product.productDescription : `${product.productDescription} (originally ${product.originalProductDescription})`}</TableCell>
        <TableCell>{product.itemSizeDesc}</TableCell>
        <TableCell align="right">{price}</TableCell>
      </TableRow>)
  })

  return (
    <Dialog maxWidth="md" fullWidth={true} open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex">
          <Box flex={1} marginRight={1}>
            <FormControl fullWidth>
              <InputLabel>Search Term</InputLabel>
              <Select variant="standard" value={searchOperator} onChange={(event) => setSearchOperator(event.target.value)}>
                {searchOperatorNodes}
              </Select>
            </FormControl>
          </Box>
          <Box flex={2} marginRight={1}>
            <TextField
              label="Description or Barcode"
              placeholder="e.g. cola or 5000123456789"
              fullWidth
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              helperText="Search Term should be at least 3 characters"
              autoFocus
              onKeyUp={handleKeyPress}
            />
          </Box>
          <Box flex={1} marginRight={1}>
            <FormControl fullWidth>
              <InputLabel>Product Pricing Type</InputLabel>
              <Select variant="standard" value={type} onChange={(event) => setType(event.target.value)}>
                {typeNodes}
              </Select>
            </FormControl>
          </Box>
          <Box position="relative" top={13}>
            <Button variant="contained" color="primary" onClick={handleSearch} disabled={searchValue.length < 3 || isSearching} style={{ marginRight: '5px' }}>
              {
                isSearching === true
                  ? <><CircularProgress color="inherit" size={20} />&nbsp;&nbsp;</>
                  : <><SearchIcon />&nbsp;</>
              }
              Search
            </Button>
            <Button variant="contained" color="secondary" onClick={resetDialog} disabled={searchValue === '' && searchOperator === searchOperators[0] && type === types[0]}><ClearIcon />&nbsp;Reset</Button>
          </Box>
        </Box>
        {
          products.length > 0
            ? <>
              {
                maxProductsExceeded &&
                <Box marginTop={3}>More than 1000 products found - please refine your search.</Box>
              }
              <Box marginTop={3}>
                <TableContainer className={classes.tableContainer}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Barcode</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Item Size</TableCell>
                        <TableCell>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{productRows}</TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
            : <Box marginTop={3}>{isSearching ? 'Searching...' : 'No products found matching search criteria.'}</Box>
        }
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => onProductSelect(selectedProduct)} disabled={!selectedProduct} color="primary">
          <CheckCircleIcon />&nbsp;Select Product
        </Button>
        <Button variant="contained" onClick={onClose} color="secondary">
          <CancelIcon />&nbsp;Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

SelectProductDialog.propTypes = {
  siteId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onProductSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  products: PropTypes.array,
  maxProductsExceeded: PropTypes.bool
}
